import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import { configPresetAPI, type WeightsSummary } from '../../../../services/configPresetAPI';
import { METRIC_LABELS, COMPONENT_LABELS } from './constants';

export interface ComparisonRow {
    key: string;
    metric: string;
    component: string;
    [presetKey: string]: any;
}

export const usePresetComparison = (initialPresets: string[] = ['balanced', 'aggressive']) => {
    const [loading, setLoading] = useState(true);
    const [weightsSummary, setWeightsSummary] = useState<WeightsSummary | null>(null);
    const [selectedPresets, setSelectedPresets] = useState<string[]>(initialPresets);
    const [tableData, setTableData] = useState<ComparisonRow[]>([]);

    const loadWeightsSummary = async () => {
        setLoading(true);
        try {
            const data = await configPresetAPI.getWeightsSummary();
            setWeightsSummary(data);
        } catch (error) {
            message.error('加载权重摘要失败');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const buildTableData = useCallback(() => {
        if (!weightsSummary) return;

        const rows: ComparisonRow[] = [];

        Object.entries(METRIC_LABELS).forEach(([metricKey, metricLabel]) => {
            const firstPresetKey = selectedPresets[0];
            if (!firstPresetKey || !weightsSummary.presets[firstPresetKey]) return;

            const components = weightsSummary.presets[firstPresetKey].weights[metricKey];
            if (!components) return;

            Object.keys(components).forEach((componentKey) => {
                const row: ComparisonRow = {
                    key: `${metricKey}-${componentKey}`,
                    metric: metricLabel,
                    component: COMPONENT_LABELS[componentKey] || componentKey,
                };

                selectedPresets.forEach((presetKey) => {
                    const presetData = weightsSummary.presets[presetKey];
                    if (presetData && presetData.weights[metricKey]) {
                        row[presetKey] = presetData.weights[metricKey][componentKey] || 0;
                    } else {
                        row[presetKey] = 0;
                    }
                });

                rows.push(row);
            });
        });

        setTableData(rows);
    }, [weightsSummary, selectedPresets]);

    useEffect(() => {
        loadWeightsSummary();
    }, []);

    useEffect(() => {
        if (weightsSummary) {
            buildTableData();
        }
    }, [weightsSummary, selectedPresets, buildTableData]);

    return {
        loading,
        weightsSummary,
        selectedPresets,
        setSelectedPresets,
        tableData,
    };
};
